import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Copy, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAGNETE = [
  { id: "wer-darf-was-atlas", label: 'Delegations-Atlas „Wer darf was?"' },
  { id: "angehoerigen-leitfaden", label: "Angehörigen-Leitfaden" },
];

const Zeile = ({ label, value, testid }) => (
  <div className="flex items-center justify-between border-b border-cream-300 py-2.5">
    <span className="text-sm text-sand-600">{label}</span>
    <span className="font-heading font-bold text-sand-800" data-testid={testid}>{value}</span>
  </div>
);

export default function AdminLeadStatistik() {
  const [magnet, setMagnet] = useState("wer-darf-was-atlas");
  const [stats, setStats] = useState(null);
  const [emails, setEmails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [affiliates, setAffiliates] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/admin/affiliate/stats`, { withCredentials: true })
      .then((res) => setAffiliates(res.data.affiliates || []))
      .catch(() => {});
  }, []);

  const copyAffLink = async (link) => {
    await navigator.clipboard.writeText(link);
    toast.success("Affiliate-Link kopiert.");
  };

  const load = async (m) => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        axios.get(`${API}/admin/leadmagnet/stats`, { withCredentials: true, params: { magnet_id: m } }),
        axios.get(`${API}/admin/leadmagnet/emails`, { withCredentials: true, params: { magnet_id: m } }),
      ]);
      setStats(s.data);
      setEmails(e.data);
    } catch {
      toast.error("Statistik konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(magnet); }, [magnet]);

  const copyEmails = async () => {
    await navigator.clipboard.writeText((emails?.emails || []).join("\n"));
    toast.success(`${emails?.count || 0} E-Mail-Adressen kopiert – fürs CRM einfügen.`);
  };

  return (
    <div className="max-w-3xl" data-testid="admin-lead-statistik">
      <h1 className="font-heading font-bold text-2xl text-sand-800 tracking-tight">Lead-Statistik</h1>
      <p className="mt-1 text-sm text-sand-600">Nur-Zahlen-Auswertung der Leadmagnet-Leads · Quelle: Website-Datenbank.</p>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <select
          value={magnet}
          onChange={(e) => setMagnet(e.target.value)}
          data-testid="lead-statistik-magnet"
          className="h-10 rounded-xl border border-cream-300 bg-white px-3 text-sm text-sand-800"
        >
          {MAGNETE.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
        <Button variant="outline" onClick={() => load(magnet)} data-testid="lead-statistik-reload">
          <RefreshCw className="h-4 w-4 mr-2" /> Aktualisieren
        </Button>
        <Button onClick={copyEmails} disabled={!emails?.count} data-testid="lead-statistik-copy-emails">
          <Copy className="h-4 w-4 mr-2" /> E-Mail-Liste kopieren ({emails?.count ?? "–"})
        </Button>
      </div>

      {loading ? (
        <p className="mt-8 text-sand-500 text-sm">Lade Zahlen …</p>
      ) : stats && (
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl bg-white border border-cream-300 p-5">
            <h2 className="font-heading font-bold text-sand-800">Überblick</h2>
            <Zeile label="Leads gesamt" value={stats.gesamt} testid="stat-gesamt" />
            <Zeile label="Newsletter-Opt-in · ja" value={stats.newsletter_opt_in_ja} testid="stat-optin" />
            <Zeile label="davon Double-Opt-in bestätigt" value={stats.davon_double_opt_in_bestaetigt} testid="stat-doi" />
            <Zeile label="Bestätigt gesamt" value={stats.bestaetigt_gesamt} testid="stat-confirmed" />
            <Zeile label="Firma/Pflegedienst gefüllt" value={stats.firma_gefuellt} testid="stat-firma" />
            <Zeile label="Telefon gefüllt" value={stats.telefon_gefuellt} testid="stat-telefon" />
            <Zeile label="Top-Gruppe: PDL/Heim + Firma" value={stats.top_gruppe_pdl_heim_mit_firma} testid="stat-top" />
            <Zeile label="… davon mit Opt-in" value={stats.davon_mit_newsletter_opt_in} testid="stat-top-optin" />
          </div>
          <div className="rounded-2xl bg-white border border-cream-300 p-5">
            <h2 className="font-heading font-bold text-sand-800">Nach Monat & Typ</h2>
            {Object.entries(stats.nach_monat_und_typ || {}).map(([k, v]) => (
              <Zeile key={k} label={k.replace("|", " · ")} value={v} />
            ))}
            <h2 className="font-heading font-bold text-sand-800 mt-5">Nach Rolle</h2>
            {Object.entries(stats.nach_rolle || {}).map(([k, v]) => (
              <Zeile key={k} label={k} value={v} />
            ))}
          </div>
        </div>
      )}

      <p className="mt-6 text-xs text-sand-500">
        Die E-Mail-Liste ist für den CRM-Abgleich gedacht (Teil 2 des CRM-Prompts). Double-Opt-in-Daten
        stehen im Feld confirmed_at je Lead.
      </p>

      <h2 className="mt-10 font-heading font-bold text-xl text-sand-800 tracking-tight">Affiliate / Creator</h2>
      <p className="mt-1 text-sm text-sand-600">Klicks, Anfragen und Buchungen über Creator-Links (Rabatt wird im Checkout automatisch angewendet).</p>
      {affiliates.length === 0 ? (
        <p className="mt-4 text-sm text-sand-500">Noch keine Affiliate-Codes angelegt.</p>
      ) : (
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          {affiliates.map((a) => (
            <div key={a.code} className="rounded-2xl bg-white border border-cream-300 p-5" data-testid={`affiliate-card-${a.code}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-bold text-sand-800">{a.name} · {a.code} (−{a.pct} %)</h3>
                <Button size="sm" variant="outline" onClick={() => copyAffLink(a.link)} data-testid={`affiliate-copy-${a.code}`}>
                  <Copy className="h-4 w-4 mr-1.5" /> Link
                </Button>
              </div>
              <p className="mt-1 text-xs text-sand-500 break-all">{a.link}</p>
              <Zeile label="Klicks auf den Link" value={a.clicks} testid={`aff-clicks-${a.code}`} />
              <Zeile label="Beratungsanfragen (Leads)" value={a.leads} testid={`aff-leads-${a.code}`} />
              <Zeile label="Buchungen mit Code" value={a.bookings} testid={`aff-bookings-${a.code}`} />
              <Zeile label="Umsatz (bezahlt)" value={`€ ${Number(a.revenue).toLocaleString("de-DE", { minimumFractionDigits: 2 })}`} testid={`aff-revenue-${a.code}`} />
              <Zeile label={`Provision (${a.commission_pct} %)`} value={`€ ${Number(a.provision).toLocaleString("de-DE", { minimumFractionDigits: 2 })}`} testid={`aff-provision-${a.code}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
